import { Button } from "@/components/ui/button"
import { MapPin, Phone, Mail, Facebook, Twitter, Instagram, Linkedin, Youtube } from "lucide-react"
import { VisitorCount } from "@/components/visitor-count"
import Script from "next/script"
import { RevealOnScroll } from "@/components/reveal-on-scroll"

export function Footer() {
  return (
    <footer className="bg-foreground text-background">
      <div className="w-full px-4 sm:px-6 py-4 sm:py-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 sm:gap-8">
          {/* Institution Info */}
          <div className="lg:col-span-1">
            <RevealOnScroll animationClass="animate-slide-in-left">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <img src="/images/logo.webp" alt="LOKBHARATI GROUP OF SCHOOLS & COLLEGES Logo" width="60" height="60" loading="lazy" />
                <div>
                  <h3 className="font-serif text-lg sm:text-xl font-bold">LOKBHARATI GROUP OF SCHOOLS & COLLEGES</h3>
                  <p className="text-xs sm:text-sm text-background/70"></p>
                </div>
              </div>
              <div className="flex gap-2 sm:gap-4">
                <Button size="sm" variant="ghost" className="text-background hover:bg-background/10 p-2">
                  <a href="https://www.facebook.com/lokbhartieducation#" aria-label="Facebook">
                    <Facebook className="h-4 w-4" />
                  </a>
                </Button>
                <Button size="sm" variant="ghost" className="text-background hover:bg-background/10 p-2">
                  <a href="tel:02502431706" aria-label="Phone">
                    <Phone className="h-4 w-4" />
                  </a>
                </Button>
                <Button size="sm" variant="ghost" className="text-background hover:bg-background/10 p-2">
                  <Instagram className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-background hover:bg-background/10 p-2">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-background hover:bg-background/10 p-2">
                  <Youtube className="h-4 w-4" />
                </Button>
              </div>
            </RevealOnScroll>
          </div>

          {/* JustDial Badge */}
          <div className="lg:col-span-1">
            <RevealOnScroll animationClass="animate-fade-in delay-100">
              <h4 className="font-semibold text-background mb-4 sm:mb-6 text-base sm:text-lg">Find Us</h4>
              <div className="bg-white rounded-lg overflow-hidden h-[150px] w-full max-w-[232px]">
                <div className="w-full h-full flex items-center justify-center">
                  <div
                    id="JdBadge"
                    data-s1="b2"
                    data-s2="Y3RyY3QlNDAlNDAlNDAwMjJQWFgyMiUyQlhYMjIlMkIxMzA0MTcxNzUzMjMlMkJVNkY0"
                    className="w-full h-full"
                    style={{ transform: 'scale(1.3)', transformOrigin: 'center' }}
                  />
                  <div id="JdBadgeDest" className="w-full h-full" />
                </div>
              </div>
            </RevealOnScroll>
          </div>


          {/* Google Maps */}
          <div className="lg:col-span-1">
            <RevealOnScroll animationClass="animate-fade-in delay-200">
              <h4 className="font-semibold text-background mb-4 sm:mb-6 text-base sm:text-lg">Location</h4>
              <div className="rounded-lg overflow-hidden h-[150px] w-full max-w-[230px]">
                <iframe
                  title="Google Maps Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3762.659036985729!2d72.81994097521422!3d19.42713258185113!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7a96a2efa5a2f%3A0xea46491bb51fb4b7!2sLokmanya%20High%20School%20%26%20Junior%20College!5e0!3m2!1sen!2sin!4v1763032831916!5m2!1sen!2sin"
                  className="w-full h-full rounded-lg"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </RevealOnScroll>
          </div>

          <div className="lg:col-span-1">
            <RevealOnScroll animationClass="animate-slide-in-right">
              <h4 className="font-semibold text-background mb-4 sm:mb-6 text-base sm:text-lg">Contact Info</h4>
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-background/60 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-background/80 text-sm leading-relaxed">
                      Vijay Nagar Nallsopara (E.) 401209 Palghar
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-background/60" />
                  <a href="tel:02502431706" className="text-background/80 text-sm hover:text-background transition-colors">02502431706</a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-background/60" />
                  <a href="https://mail.google.com/mail/?view=cm&fs=1&to=lokbhartigroup.contact@gmail.com" target="_blank" rel="noopener noreferrer" className="text-background/80 text-sm hover:text-background transition-colors">lokbhartigroup.contact@gmail.com</a>
                </div>
              </div>
            </RevealOnScroll>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-background/20">
          <RevealOnScroll animationClass="animate-fade-in delay-300">
            <div className="relative flex flex-col md:flex-row items-center justify-center gap-2 py-2">
              <div className="md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2">
                <VisitorCount compact />
              </div>
              <p className="text-background/60 text-xs text-center">
                © 2026 <a href="https://bitbluetech.com/" target="_blank" rel="noopener noreferrer">Bitblue Technology</a>. All rights reserved.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </div>

      {/* JustDial Script */}
      <Script
        src="https://www.justdial.com/Jd-Widget"
        strategy="afterInteractive"
      />
    </footer>
  )
}